import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart3,
  RefreshCw,
  Loader2,
  TrendingUp,
  Clock,
  Target,
  Flame,
  Trophy,
  Calendar,
  Sun,
  Zap,
  AlertCircle,
  Lightbulb,
  CheckCircle2,
  Activity
} from 'lucide-react';
import { Button, Card, EmptyState } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  analyticsService,
  UserAnalyticsSummaryDto,
  SessionHeatmapSlotDto,
  PredictiveInsightDto
} from '../../services';
import './Analytics.css';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const DAY_NAMES_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const formatHour = (hour: number): string => {
  if (hour === 0) return '12am';
  if (hour === 12) return '12pm';
  if (hour < 12) return `${hour}am`;
  return `${hour - 12}pm`;
};

const getCategoryIcon = (category: string) => {
  switch (category.toLowerCase()) {
    case 'productivity':
      return <TrendingUp size={18} />;
    case 'scheduling':
      return <Calendar size={18} />;
    case 'wellness':
      return <Activity size={18} />;
    case 'goal':
      return <Target size={18} />;
    default:
      return <Lightbulb size={18} />;
  }
};

const getConfidenceClass = (confidence: number): string => {
  if (confidence >= 80) return 'analytics__confidence--high';
  if (confidence >= 50) return 'analytics__confidence--medium';
  return 'analytics__confidence--low';
};

const getHeatIntensity = (value: number, maxValue: number): number => {
  if (maxValue === 0 || value === 0) return 0;
  const normalized = value / maxValue;
  if (normalized <= 0.2) return 1;
  if (normalized <= 0.4) return 2;
  if (normalized <= 0.6) return 3;
  if (normalized <= 0.8) return 4;
  return 5;
};

export const Analytics: React.FC = () => {
  useAuth();

  const [summary, setSummary] = useState<UserAnalyticsSummaryDto | null>(null);
  const [heatmap, setHeatmap] = useState<SessionHeatmapSlotDto[]>([]);
  const [predictions, setPredictions] = useState<PredictiveInsightDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [summaryData, heatmapData, predictionsData] = await Promise.all([
        analyticsService.getSummary(),
        analyticsService.getHeatmap().catch(() => []),
        analyticsService.getPredictions().catch(() => [])
      ]);

      setSummary(summaryData);
      setHeatmap(heatmapData);
      setPredictions(predictionsData);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
      setError('Failed to load analytics. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const handleRecalculate = async () => {
    try {
      setIsRecalculating(true);
      await analyticsService.recalculate();
      await fetchAnalytics();
    } catch (err) {
      console.error('Failed to recalculate analytics:', err);
      setError('Failed to recalculate. Please try again.');
    } finally {
      setIsRecalculating(false);
    }
  };

  // Build a 7x24 grid lookup from heatmap data
  const heatmapGrid: Record<string, SessionHeatmapSlotDto> = {};
  let maxSessionCount = 0;
  heatmap.forEach(slot => {
    const key = `${slot.dayOfWeek}-${slot.hourOfDay}`;
    heatmapGrid[key] = slot;
    if (slot.sessionCount > maxSessionCount) {
      maxSessionCount = slot.sessionCount;
    }
  });

  // Only show a subset of hour labels to avoid clutter
  const hourLabels = [0, 3, 6, 9, 12, 15, 18, 21];

  if (isLoading) {
    return (
      <AppLayout pageTitle="Analytics">
        <div className="analytics">
          <div className="analytics__loading">
            <Loader2 size={32} className="analytics__loading-spinner" />
            <p>Analyzing your creative data...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error && !summary) {
    return (
      <AppLayout pageTitle="Analytics">
        <div className="analytics">
          <div className="analytics__error">
            <AlertCircle size={32} />
            <p>{error}</p>
            <Button onClick={fetchAnalytics}>Try Again</Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!summary) {
    return (
      <AppLayout pageTitle="Analytics">
        <div className="analytics">
          <div className="analytics__main">
            <EmptyState
              icon={<BarChart3 size={56} />}
              title="No analytics yet"
              message="Start working on projects and logging sessions to see your creative analytics and patterns here."
              action={{
                label: 'Calculate Analytics',
                onClick: handleRecalculate
              }}
            />
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="Analytics">
      <div className="analytics">
        {/* Ambient background */}
        <div className="analytics__ambient">
          <div className="analytics__ambient-shape analytics__ambient-shape--1" />
          <div className="analytics__ambient-shape analytics__ambient-shape--2" />
        </div>

        <main className="analytics__main">
          {/* Header with recalculate */}
          <div className="analytics__header">
            <div className="analytics__header-info">
              <p className="analytics__subtitle">Your creative patterns at a glance</p>
              {summary.lastCalculatedAt && (
                <span className="analytics__last-updated">
                  <Clock size={12} />
                  Updated {new Date(summary.lastCalculatedAt).toLocaleDateString()}
                </span>
              )}
            </div>
            <Button
              variant="ghost"
              onClick={handleRecalculate}
              disabled={isRecalculating}
              className="analytics__recalculate-btn"
            >
              <RefreshCw size={16} className={isRecalculating ? 'analytics__spin' : ''} />
              {isRecalculating ? 'Recalculating...' : 'Recalculate'}
            </Button>
          </div>

          {error && (
            <div className="analytics__inline-error">
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {/* Summary Cards */}
          <section className="analytics__section">
            <h2 className="analytics__section-title">
              <BarChart3 size={20} />
              Overview
            </h2>
            <div className="analytics__stats-grid">
              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--purple">
                  <Target size={20} />
                </div>
                <div className="analytics__stat-value">{summary.totalProjects}</div>
                <div className="analytics__stat-label">Total Projects</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--green">
                  <CheckCircle2 size={20} />
                </div>
                <div className="analytics__stat-value">{summary.completedProjects}</div>
                <div className="analytics__stat-label">Completed</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--blue">
                  <Activity size={20} />
                </div>
                <div className="analytics__stat-value">{summary.totalSessions}</div>
                <div className="analytics__stat-label">Sessions</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--orange">
                  <Clock size={20} />
                </div>
                <div className="analytics__stat-value">{summary.totalSessionMinutes}</div>
                <div className="analytics__stat-label">Total Minutes</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--teal">
                  <TrendingUp size={20} />
                </div>
                <div className="analytics__stat-value">{summary.averageSessionMinutes}m</div>
                <div className="analytics__stat-label">Avg Session</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--pink">
                  <Zap size={20} />
                </div>
                <div className="analytics__stat-value">{Math.round(summary.completionRate)}%</div>
                <div className="analytics__stat-label">Completion Rate</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--coral">
                  <Flame size={20} />
                </div>
                <div className="analytics__stat-value">{summary.currentStreak}</div>
                <div className="analytics__stat-label">Current Streak</div>
              </Card>

              <Card variant="default" padding="md" className="analytics__stat-card">
                <div className="analytics__stat-icon analytics__stat-icon--gold">
                  <Trophy size={20} />
                </div>
                <div className="analytics__stat-value">{summary.longestStreak}</div>
                <div className="analytics__stat-label">Longest Streak</div>
              </Card>
            </div>
          </section>

          {/* Heatmap */}
          <section className="analytics__section">
            <h2 className="analytics__section-title">
              <Calendar size={20} />
              Session Heatmap
            </h2>
            <Card variant="default" padding="md" className="analytics__heatmap-card">
              <p className="analytics__heatmap-desc">When you work throughout the week</p>
              <div className="analytics__heatmap-wrapper">
                <div className="analytics__heatmap">
                  {/* Hour labels row */}
                  <div className="analytics__heatmap-corner" />
                  {Array.from({ length: 24 }, (_, h) => (
                    <div
                      key={`hour-${h}`}
                      className={`analytics__heatmap-hour-label ${hourLabels.includes(h) ? '' : 'analytics__heatmap-hour-label--hidden'}`}
                    >
                      {hourLabels.includes(h) ? formatHour(h) : ''}
                    </div>
                  ))}

                  {/* Day rows */}
                  {DAY_NAMES.map((day, dayIndex) => (
                    <React.Fragment key={day}>
                      <div className="analytics__heatmap-day-label">{day}</div>
                      {Array.from({ length: 24 }, (_, hourIndex) => {
                        const slot = heatmapGrid[`${dayIndex}-${hourIndex}`];
                        const count = slot?.sessionCount || 0;
                        const intensity = getHeatIntensity(count, maxSessionCount);
                        return (
                          <div
                            key={`${dayIndex}-${hourIndex}`}
                            className={`analytics__heatmap-cell analytics__heatmap-cell--${intensity}`}
                            title={`${DAY_NAMES_FULL[dayIndex]} ${formatHour(hourIndex)}: ${count} session${count !== 1 ? 's' : ''}${slot ? `, ${slot.totalMinutes}min` : ''}`}
                          />
                        );
                      })}
                    </React.Fragment>
                  ))}
                </div>

                {/* Legend */}
                <div className="analytics__heatmap-legend">
                  <span className="analytics__heatmap-legend-label">Less</span>
                  <div className="analytics__heatmap-cell analytics__heatmap-cell--0 analytics__heatmap-legend-cell" />
                  <div className="analytics__heatmap-cell analytics__heatmap-cell--1 analytics__heatmap-legend-cell" />
                  <div className="analytics__heatmap-cell analytics__heatmap-cell--2 analytics__heatmap-legend-cell" />
                  <div className="analytics__heatmap-cell analytics__heatmap-cell--3 analytics__heatmap-legend-cell" />
                  <div className="analytics__heatmap-cell analytics__heatmap-cell--4 analytics__heatmap-legend-cell" />
                  <div className="analytics__heatmap-cell analytics__heatmap-cell--5 analytics__heatmap-legend-cell" />
                  <span className="analytics__heatmap-legend-label">More</span>
                </div>
              </div>
            </Card>
          </section>

          {/* Best Times */}
          <section className="analytics__section">
            <h2 className="analytics__section-title">
              <Sun size={20} />
              Best Work Times
            </h2>
            <div className="analytics__best-times">
              <Card variant="default" padding="md" className="analytics__best-time-card">
                <div className="analytics__best-time-icon">
                  <Clock size={24} />
                </div>
                <div className="analytics__best-time-info">
                  <div className="analytics__best-time-value">
                    {formatHour(summary.bestWorkHour)}
                  </div>
                  <div className="analytics__best-time-label">Best Hour</div>
                </div>
              </Card>

              <Card variant="default" padding="md" className="analytics__best-time-card">
                <div className="analytics__best-time-icon">
                  <Calendar size={24} />
                </div>
                <div className="analytics__best-time-info">
                  <div className="analytics__best-time-value">
                    {DAY_NAMES_FULL[summary.bestWorkDay] || 'N/A'}
                  </div>
                  <div className="analytics__best-time-label">Best Day</div>
                </div>
              </Card>
            </div>
          </section>

          {/* Insights */}
          <section className="analytics__section">
            <h2 className="analytics__section-title">
              <Lightbulb size={20} />
              Insights
            </h2>
            <div className="analytics__insights">
              {summary.stageStuckMost && (
                <Card variant="default" padding="md" className="analytics__insight-card">
                  <div className="analytics__insight-icon analytics__insight-icon--warning">
                    <AlertCircle size={20} />
                  </div>
                  <div className="analytics__insight-content">
                    <div className="analytics__insight-title">Stage Bottleneck</div>
                    <div className="analytics__insight-desc">
                      You tend to spend the most time in the <strong>{summary.stageStuckMost}</strong> stage. Consider breaking this phase into smaller tasks.
                    </div>
                  </div>
                </Card>
              )}

              <Card variant="default" padding="md" className="analytics__insight-card">
                <div className="analytics__insight-icon analytics__insight-icon--info">
                  <TrendingUp size={20} />
                </div>
                <div className="analytics__insight-content">
                  <div className="analytics__insight-title">Average Completion Time</div>
                  <div className="analytics__insight-desc">
                    Your projects take an average of <strong>{summary.averageCompletionDays} days</strong> to complete from start to finish.
                  </div>
                </div>
              </Card>
            </div>
          </section>

          {/* Predictions */}
          {predictions.length > 0 && (
            <section className="analytics__section">
              <h2 className="analytics__section-title">
                <Zap size={20} />
                AI Predictions
              </h2>
              <div className="analytics__predictions">
                {predictions.map((prediction, index) => (
                  <Card
                    key={index}
                    variant="default"
                    padding="md"
                    className="analytics__prediction-card"
                    style={{ animationDelay: `${index * 0.08}s` }}
                  >
                    <div className="analytics__prediction-header">
                      <div className="analytics__prediction-category">
                        {getCategoryIcon(prediction.category)}
                        <span>{prediction.category}</span>
                      </div>
                      <div className={`analytics__confidence ${getConfidenceClass(prediction.confidence)}`}>
                        {prediction.confidence}%
                      </div>
                    </div>
                    <div className="analytics__prediction-title">{prediction.title}</div>
                    <div className="analytics__prediction-desc">{prediction.description}</div>
                    <div className="analytics__confidence-bar">
                      <div
                        className={`analytics__confidence-fill ${getConfidenceClass(prediction.confidence)}`}
                        style={{ width: `${prediction.confidence}%` }}
                      />
                    </div>
                  </Card>
                ))}
              </div>
            </section>
          )}
        </main>
      </div>
    </AppLayout>
  );
};

export default Analytics;
